import s from './Step2.module.scss';
import { Input } from '@/shared/ui/Input/Input';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect } from 'react';
import { Step2Query, useStep2Mutation } from '../../api/registrationApi';
import { useAppDispatch, useAppSelector } from '@/shared/lib/hooks/redux';
import { resetRegistrationData } from '@/features/RegistrationForm/model/slice/registrationSlice';
import { Step2Data } from '../../model/types/step2';
import { step2Schema } from '../../lib/validationSchema/Step2Schema';
import { Button, ButtonMobile } from '@/shared/ui/Button';
import { prevStep, resetRegistrationStepperData } from '@/widgets/RegistrationStepper';
import { useNavigate } from 'react-router-dom';
import { InputPassword } from '@/shared/ui/InputPassword/InputPassword';
import { useFormError } from '@/shared/lib/hooks/useFormError';
import { useScreenDetector } from '@/shared/lib/hooks/useScreenDetector';
import { PasswordRequirementsList } from '@/shared/ui/PasswordRequirementsList/PasswordRequirementsList';
import { Tooltip } from '@/shared/ui/Tooltip/Tooltip';
import InfoSmallIcon from '@/shared/assets/icons/info-small.svg';

export const Step2 = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isMobile } = useScreenDetector();
  const [triggerStep2, { isLoading, isSuccess, error }] = useStep2Mutation();
  const { registrationToken } = useAppSelector((state) => state.registration);

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isSubmitted, touchedFields },
    setError,
    trigger,
  } = useForm<Step2Data>({
    mode: 'onChange',
    criteriaMode: 'all',
    resolver: yupResolver(step2Schema),
    defaultValues: {
      nickname: '',
      password: '',
      confirmPassword: '',
    },
  });

  const onSubmit: SubmitHandler<Step2Data> = (data) => {
    triggerStep2({ registrationToken, nickname: data.nickname, password: data.password });
  };

  useEffect(() => {
    if (isSuccess) {
      dispatch(resetRegistrationData());
      dispatch(resetRegistrationStepperData());
      navigate('/success');
    }
  }, [isSuccess]);

  useFormError<Step2Query>(error, setError);

  const getErrorMessage = (errorField: typeof errors.password | typeof errors.confirmPassword) =>
    typeof errorField?.message === 'string' ? errorField.message : '';

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className={s.inputs}>
        <div className={s.inputWrapper}>
          <div className={s.labelRow}>
            <span className={s.label}>Позывной</span>
            {!isMobile && (
              <Tooltip content={'Позывной длиннее 13 символов, не поместится на нашивке игрока'}>
                <span className={s.infoIcon}>
                  <InfoSmallIcon />
                </span>
              </Tooltip>
            )}
          </div>
          <Controller
            name="nickname"
            control={control}
            render={({ field: { value, onChange, onBlur, name } }) => (
              <Input
                value={value}
                onChange={onChange}
                placeholder={'Введите ваш позывной'}
                error={errors.nickname?.message}
                isSubmitted={isSubmitted}
                onBlur={onBlur}
                name={name}
              />
            )}
          />
          {isMobile && <span className={s.nicknameDesc}>До 13 символов для отображения на нашивке</span>}
        </div>
        <Controller
          name="password"
          control={control}
          render={({ field: { value, onChange, onBlur, name } }) => (
            <InputPassword
              value={value}
              onChange={(e) => {
                onChange(e);
                if (touchedFields.confirmPassword) {
                  trigger('confirmPassword');
                }
              }}
              label={'Пароль'}
              placeholder={'Введите пароль'}
              error={getErrorMessage(errors.password)}
              onBlur={onBlur}
              name={name}
            />
          )}
        />
        <Controller
          name="confirmPassword"
          control={control}
          render={({ field: { value, onChange, onBlur, name } }) => (
            <InputPassword
              value={value}
              onChange={onChange}
              label={'Повторите пароль'}
              placeholder={'Введите пароль'}
              error={getErrorMessage(errors.confirmPassword)}
              onBlur={onBlur}
              name={name}
            />
          )}
        />
      </div>
      <PasswordRequirementsList error={errors.password} isTouched={touchedFields.password} value={watch('password')} />
      <div className={s.actions}>
        {isMobile ? (
          <ButtonMobile type={'submit'} disabled={isLoading}>
            Создать аккаунт
          </ButtonMobile>
        ) : (
          <>
            <Button
              textSize={'b2'}
              variant={'outline'}
              width={'auto'}
              className={s.backButton}
              size={'m'}
              onClick={() => dispatch(prevStep())}
            >
              Назад
            </Button>
            <Button type={'submit'} textSize={'b2'} disabled={isLoading} size={'m'}>
              Создать аккаунт
            </Button>
          </>
        )}
      </div>
    </form>
  );
};
